import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Plus, Search, Trash2, Edit3 } from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const APPROVAL_TYPES = ['Standards Institute', 'Ministry of Health', 'Ministry of Agriculture', 'Ministry of Communications', 'Ministry of Transport', 'Other'];
const STATUSES = ['WAITING', 'SUBMITTED', 'ACCEPTED', 'REJECTED'];
const STATUS_TONE = { WAITING: 'warning', SUBMITTED: 'info', ACCEPTED: 'emerald', REJECTED: 'danger' };

const emptyForm = () => ({
  customerName: '', phone: '', approvalType: 'Standards Institute', customApprovalType: '',
  approvalNumber: '', authority: '', trackingNumber: '', dateSubmitted: '', dateAccepted: '',
  approvalStatus: 'WAITING', notes: '',
});

export default function ErpApprovals() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/approvals');
      setRows(r.data.approvals || []);
    } catch { toast({ title: 'Failed to load approvals', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter && (r.approvalStatus || 'WAITING') !== statusFilter) return false;
    if (q.trim()) {
      const t = q.toLowerCase();
      return [r.customerName, r.approvalNumber, r.approvalType, r.authority, r.trackingNumber, r.notes]
        .some((v) => (v || '').toLowerCase().includes(t));
    }
    return true;
  }), [rows, q, statusFilter]);

  const counts = useMemo(() => ({
    total: filtered.length,
    waiting: filtered.filter((r) => (r.approvalStatus || 'WAITING') === 'WAITING').length,
    accepted: filtered.filter((r) => r.approvalStatus === 'ACCEPTED').length,
  }), [filtered]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      let label = 'No date';
      let ts = 0;
      if (r.dateSubmitted) {
        const d = new Date(r.dateSubmitted);
        if (!isNaN(d.getTime())) {
          label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
          ts = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        }
      } else if (r.month) {
        label = r.month;
        ts = 1;
      }
      if (!map.has(label)) map.set(label, { label, ts, rows: [] });
      map.get(label).rows.push(r);
    });
    return Array.from(map.values()).sort((a, b) => b.ts - a.ts);
  }, [filtered]);

  const openEdit = (r) => {
    const f = emptyForm();
    Object.keys(f).forEach((k) => { f[k] = r[k] || (k === 'approvalStatus' ? 'WAITING' : ''); });
    setForm(f);
    setEditingId(r.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.customerName.trim()) {
      toast({ title: 'Customer name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/documents/${editingId}`, form);
        toast({ title: 'Approval updated' });
      } else {
        await api.post('/records', { ...form, category: 'APPROVAL' });
        toast({ title: 'Approval added' });
      }
      setShowForm(false);
      setForm(emptyForm());
      setEditingId(null);
      load();
    } catch { toast({ title: 'Failed to save', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this approval?')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast({ title: 'Approval deleted' });
      load();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  return (
    <ErpShell title="Approvals">
      <ErpPageHeader
        eyebrow="Compliance"
        title="Approvals"
        subtitle="Government & authority approvals — all in one place."
        icon={ClipboardCheck}
        actions={
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gg-text-dim)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, number…" className="gg-input ps-9" style={{ width: 240 }} data-testid="approvals-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="gg-input" style={{ width: 160 }} data-testid="approvals-status-filter">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => { setForm(emptyForm()); setEditingId(null); setShowForm(true); }} className="gg-btn-primary" data-testid="new-approval-btn">
              <Plus size={14} /> New Approval
            </button>
          </>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total', value: counts.total, tone: 'neutral' },
          { label: 'Waiting', value: counts.waiting, tone: 'warning' },
          { label: 'Accepted', value: counts.accepted, tone: 'emerald' },
        ].map((s) => (
          <div key={s.label} className="gg-card p-4">
            <p className="gg-eyebrow">{s.label}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="gg-tabular" style={{ fontSize: 26, fontWeight: 700, color: 'var(--gg-text)' }}>{s.value}</span>
              <span className={`gg-chip gg-chip-${s.tone}`}>{s.label.toLowerCase()}</span>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="gg-card p-12 animate-pulse" />
      ) : filtered.length === 0 ? (
        <ErpEmpty
          icon={ClipboardCheck}
          title={q || statusFilter ? 'No approvals match your filters' : 'No approvals yet'}
          subtitle="Track standards, health and other authority approvals here."
          action={<button onClick={() => setShowForm(true)} className="gg-btn-primary"><Plus size={14} /> Add Approval</button>}
        />
      ) : (
        <div className="space-y-7" data-testid="approvals-groups">
          {groups.map((g) => (
            <div key={g.label} data-testid={`month-section-${g.label.replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-3 mb-2 px-1">
                <h3 className="gg-section-title" style={{ fontSize: 17 }}>{g.label}</h3>
                <span className="gg-chip gg-chip-accent gg-tabular">{g.rows.length} approval{g.rows.length > 1 ? 's' : ''}</span>
              </div>
              <div className="gg-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="gg-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Type</th>
                        <th>Approval #</th>
                        <th>Tracking #</th>
                        <th>Shipping Company</th>
                        <th>Submitted</th>
                        <th>Accepted</th>
                        <th>Status</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r, idx) => (
                        <motion.tr key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.25) }} data-testid={`approval-row-${r.id}`}>
                          <td>
                            <p className="font-semibold">{r.customerName || '—'}</p>
                            <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{r.phone || ''}</p>
                          </td>
                          <td style={{ color: 'var(--gg-text-secondary)' }}>{r.approvalType === 'Other' ? (r.customApprovalType || 'Other') : (r.approvalType || '—')}</td>
                          <td className="gg-tabular">{r.approvalNumber || '—'}</td>
                          <td className="gg-tabular">{r.trackingNumber || '—'}</td>
                          <td style={{ color: 'var(--gg-text-secondary)' }}>{r.authority || '—'}</td>
                          <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.dateSubmitted || '—'}</td>
                          <td className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{r.dateAccepted || '—'}</td>
                          <td><span className={`gg-chip gg-chip-${STATUS_TONE[r.approvalStatus] || 'warning'}`}>{r.approvalStatus || 'WAITING'}</span></td>
                          <td>
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEdit(r)} className="gg-btn-ghost" data-testid={`edit-approval-${r.id}`}><Edit3 size={13} /></button>
                              <button onClick={() => remove(r.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`delete-approval-${r.id}`}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-20" onClick={() => !saving && setShowForm(false)}>
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="gg-cmdk-panel p-6"
            style={{ width: 'min(680px, 92vw)', maxHeight: '85vh', overflowY: 'auto' }}
            data-testid="approval-form"
          >
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{editingId ? 'Edit' : 'New'}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 24 }}>{editingId ? 'Update' : 'Add'} Approval</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <F label="Customer Name *"><input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} className="gg-input" data-testid="form-customerName" /></F>
              <F label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="gg-input" data-testid="form-phone" /></F>
              <F label="Approval Type">
                <select value={form.approvalType} onChange={(e) => setForm({ ...form, approvalType: e.target.value })} className="gg-input" data-testid="form-approvalType">
                  {APPROVAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </F>
              {form.approvalType === 'Other' && (
                <F label="Custom Type"><input value={form.customApprovalType} onChange={(e) => setForm({ ...form, customApprovalType: e.target.value })} className="gg-input" data-testid="form-customApprovalType" /></F>
              )}
              <F label="Approval Number"><input value={form.approvalNumber} onChange={(e) => setForm({ ...form, approvalNumber: e.target.value })} className="gg-input" data-testid="form-approvalNumber" /></F>
              <F label="Tracking Number"><input value={form.trackingNumber} onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })} className="gg-input" data-testid="form-trackingNumber" /></F>
              <F label="Shipping Company"><input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} className="gg-input" data-testid="form-authority" /></F>
              <F label="Date Submitted"><input type="date" value={form.dateSubmitted} onChange={(e) => setForm({ ...form, dateSubmitted: e.target.value })} className="gg-input" data-testid="form-dateSubmitted" /></F>
              <F label="Date Accepted"><input type="date" value={form.dateAccepted} onChange={(e) => setForm({ ...form, dateAccepted: e.target.value })} className="gg-input" data-testid="form-dateAccepted" /></F>
              <F label="Status">
                <select value={form.approvalStatus} onChange={(e) => setForm({ ...form, approvalStatus: e.target.value })} className="gg-input" data-testid="form-approvalStatus">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <div className="col-span-2">
                <F label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="gg-input" rows={2} style={{ height: 'auto', padding: '10px 14px' }} data-testid="form-notes" /></F>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} disabled={saving} className="gg-btn-secondary" data-testid="form-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="gg-btn-primary" data-testid="form-save">
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save Approval'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}

const F = ({ label, children }) => (
  <div>
    <label className="gg-eyebrow block mb-1">{label}</label>
    {children}
  </div>
);
